import { CustomTooltip, IconButton, NormalButton, ViewMoreButton } from '@/shared/ui/components';
import { editIconV2, viewIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { RMA_STATUSES, convertRmaStatusToLocalStatus } from '@/entities/rma';
import { zeroPad, gettextRmaStatus } from '@/shared/lib';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import ReminderIcon from '../ui/rmaRequestTable/assets/images/reminder-icon.svg';

/**
 * Flattens RMA tickets from the API into the row shape used by the RMA request table. Each ticket keeps its id and rmaId, derives a display status through convertRmaStatusToLocalStatus from status, and pulls the facility name, sender and recipient device lists, requestedAt, the latest return label shipment date and the clinic shipment reminder flag.
 *
 * @param rmaTickets - The RMA tickets returned by the API.
 * @returns Table rows built from the tickets.
 */
const formatRmaTickets = (rmaTickets: any) =>
  rmaTickets.map((ticket: any) => {
    const rmaStatus = convertRmaStatusToLocalStatus(ticket.status);
    return {
      id: ticket.id,
      rmaId: ticket.rmaId,
      status: rmaStatus,
      facility: ticket?.facility?.name,
      outboundDevices: ticket?.senderInformation?.devices,
      inboundDevices: ticket?.recipientInformation?.devices,
      requestedAt: ticket?.requestedAt,
      returnedDate: ticket?.recipientInformation?.latestLabel?.shipmentDate,
      isReminder: ticket.clinicShipmentReminder?.isReminder,
    };
  });

/**
 * Maps an internal RMA ticket status onto the label used by the RMA list filters and API. Switches PENDING to New, APPROVED to Approved and REJECTED to Rejected, returning undefined for any other status.
 *
 * @param status - The internal RMA status constant to translate.
 * @returns The external RMA status label, or undefined for an unrecognised status.
 */
const convertLocalStatusToRmaStatus = (status: any) => {
  switch (status) {
    case RMA_STATUSES.PENDING: {
      return 'New';
    }
    case RMA_STATUSES.APPROVED: {
      return 'Approved';
    }
    case RMA_STATUSES.REJECTED: {
      return 'Rejected';
    }
    default: {
      return undefined;
    }
  }
};

/**
 * Renders the RMA ticket status as a coloured label whose modifier class comes from the lowercased status value and whose text is translated by gettextRmaStatus. Falls back to a double-hyphen bullet for an empty status and to a skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns The status label element, or a loading skeleton.
 */
const rmaStatusFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    const prefixClass = cell ? `--${_.lowerCase(cell)}` : '';
    return (
      <div className="d-flex">
        {cell ? (
          <div className={classNames('rma-status-label', prefixClass)}>
            {gettextRmaStatus(cell)}
          </div>
        ) : (
          DOUBLE_HYPHEN_BULLET
        )}
      </div>
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the outbound or inbound device count of an RMA ticket as a text button that opens the device list drawer. Picks row.outboundDevices or row.inboundDevices from the type in the extra data, shows a double-hyphen bullet when the count is zero, and passes the chosen device array plus type to handleClickDeviceCount; a skeleton is shown until isLoadedData is true.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns The clickable device-count button, a placeholder, or a loading skeleton.
 */
const deviceCountFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, type, handleClickDeviceCount } = formatExtraData;
  const deviceCount =
    (type === 'outbound' ? row.outboundDevices?.length : row.inboundDevices?.length) || 0;
  const devices = type === 'outbound' ? row.outboundDevices : row.inboundDevices;
  return isLoadedData ? (
    deviceCount ? (
      <NormalButton
        id="device-id-btn"
        className="nowrap"
        isText
        buttonName={deviceCount}
        onClick={() => {
          handleClickDeviceCount(devices, type);
        }}
      />
    ) : (
      DOUBLE_HYPHEN_BULLET
    )
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders a date cell of the RMA request table formatted as MM/DD/YYYY through dayjs, using a double-hyphen bullet placeholder for an empty value. Shows a single-line skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns A span with the formatted date, or a loading skeleton.
 */
const dateFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? (
    <span>{cell ? dayjs(cell).format('MM/DD/YYYY') : DOUBLE_HYPHEN_BULLET}</span>
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the RMA id cell zero-padded to five digits, with a double-hyphen bullet when the id is missing. Appends a tooltipped Order Shipment reminder icon when the row's isReminder flag is set, and shows a skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns The RMA id cell with an optional reminder icon, or a loading skeleton.
 */
const rmaIdFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? (
    <div className="rma-id-row">
      <span>{cell ? zeroPad(cell, 5) : DOUBLE_HYPHEN_BULLET}</span>
      {row.isReminder && (
        <CustomTooltip placement="bottom" title={t`Order Shipment`}>
          <div>
            <IconButton
              id="reminder-icon"
              className="reminder-icon"
              iconComponent={<img src={ReminderIcon} alt="Reminder Icon" />}
              aria-label={t`Order shipment`}
            />
          </div>
        </CustomTooltip>
      )}
    </div>
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders a plain text cell of the RMA request table, substituting a double-hyphen bullet placeholder when the value is empty. Falls back to a single-line skeleton while the table's isLoadedData flag is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns A span with the cell text, or a loading skeleton.
 */
const cellFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? <span>{cell || DOUBLE_HYPHEN_BULLET}</span> : <Skeleton count={1} />;
};

/**
 * Renders the row action for an RMA ticket as a single view-more icon that opens the ticket, labelled Edit RMA while the ticket is still pending and View RMA otherwise. Returns nothing until the table reports isLoadedData, and the icon click passes the ticket's internal id to handleClickViewRma.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The RMA ticket row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying the isLoadedData flag and row callbacks.
 * @returns The view-more action button, or undefined while data is still loading.
 */
const actionIconFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, handleClickViewRma } = formatExtraData;
  if (isLoadedData) {
    return (
      <ViewMoreButton
        icons={
          [
            {
              id: `view-rma-${row.rmaId}`,
              tooltipTitle: row.status === RMA_STATUSES.PENDING ? t`Edit RMA` : t`View RMA`,
              icon: <ReactSVG src={row.status === RMA_STATUSES.PENDING ? editIconV2 : viewIcon} />,
              onClick: () => {
                handleClickViewRma(row.id);
              },
            },
          ] as any
        }
      />
    );
  }
  return undefined;
};

export {
  actionIconFormatter,
  cellFormatter,
  convertLocalStatusToRmaStatus,
  dateFormatter,
  deviceCountFormatter,
  formatRmaTickets,
  rmaIdFormatter,
  rmaStatusFormatter,
};
